import {
  CSRF_HEADER,
  isApiErrorBody,
  type AuthResponse,
  type ErrorCode,
  type LoginRequest,
  type RegisterRequest,
} from '@hutdrop/shared';

export type ClientErrorCode = ErrorCode | 'NETWORK';

const FRIENDLY: Partial<Record<ClientErrorCode, string>> = {
  NETWORK: "We couldn't connect to HutDrop. Check your connection and try again.",
  INTERNAL: 'Something went wrong. Please try again.',
};

/** Every failure surfaces as this, with a message that is safe to show to people. */
export class ApiError extends Error {
  constructor(
    readonly code: ClientErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation messages, keyed by field name. */
  get fieldErrors(): Record<string, string> {
    const fields = (this.details as { fields?: Record<string, string> } | undefined)?.fields;
    return fields ?? {};
  }
}

export interface ApiClientOptions {
  /** '' for same-origin (dev proxy), or the absolute API origin. */
  baseUrl: string;
  /** '/v1/auth' for customers, '/v1/admin/auth' for staff. */
  authPath: '/v1/auth' | '/v1/admin/auth';
  fetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Multipart upload (e.g. product photos). Mutually exclusive with `body`. */
  form?: FormData;
  /**
   * true: require a session (refreshing the access token first if needed).
   * 'optional': send the token only when signed in — for public pages that personalise (favorite hearts).
   */
  auth?: boolean | 'optional';
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** 'blob' for file downloads (e.g. CSV exports). */
  responseType?: 'json' | 'blob';
}

type Listener = (session: AuthResponse | null) => void;

const RETRYABLE_STATUS = new Set([502, 503, 504]);
const REFRESH_SKEW_MS = 30_000;

export function createApiClient(opts: ApiClientOptions) {
  const doFetch = opts.fetch ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args));
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let session: AuthResponse | null = null;
  let refreshing: Promise<AuthResponse | null> | null = null;
  const listeners = new Set<Listener>();

  function setSession(next: AuthResponse | null) {
    session = next;
    for (const l of listeners) l(next);
  }

  async function send(path: string, init: RequestInit): Promise<Response> {
    try {
      return await doFetch(`${opts.baseUrl}${path}`, { credentials: 'include', ...init });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new ApiError('NETWORK', FRIENDLY.NETWORK!, 0);
    }
  }

  async function toError(res: Response): Promise<ApiError> {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    if (isApiErrorBody(body)) {
      const { code, message, details, requestId } = body.error;
      return new ApiError(code, code === 'INTERNAL' ? FRIENDLY.INTERNAL! : message, res.status, details, requestId);
    }
    return new ApiError('INTERNAL', FRIENDLY.INTERNAL!, res.status);
  }

  /** Single-flight: concurrent callers share one refresh request. */
  function refresh(): Promise<AuthResponse | null> {
    refreshing ??= (async () => {
      try {
        const res = await send(`${opts.authPath}/refresh`, {
          method: 'POST',
          headers: { [CSRF_HEADER]: '1' },
        });
        if (res.ok) {
          const next = (await res.json()) as AuthResponse;
          setSession(next);
          return next;
        }
        if (res.status === 401 || res.status === 403) {
          setSession(null);
          return null;
        }
        throw await toError(res);
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  async function accessToken(): Promise<string | null> {
    if (session && Date.parse(session.accessTokenExpiresAt) - Date.now() > REFRESH_SKEW_MS) {
      return session.accessToken;
    }
    return (await refresh())?.accessToken ?? null;
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const maxAttempts = method === 'GET' ? 3 : 1;

    const build = async (): Promise<RequestInit> => {
      const headers: Record<string, string> = { accept: options.responseType === 'blob' ? '*/*' : 'application/json', ...options.headers };
      if (options.body !== undefined && !options.form) headers['content-type'] = 'application/json';
      if (options.auth === true || (options.auth === 'optional' && session)) {
        const token = await accessToken();
        if (token) headers.authorization = `Bearer ${token}`;
        else if (options.auth === true) throw new ApiError('UNAUTHENTICATED', 'Please sign in to continue.', 401);
      }
      return {
        method,
        headers,
        body: options.form ?? (options.body === undefined ? null : JSON.stringify(options.body)),
        signal: options.signal ?? null,
      };
    };

    for (let attempt = 1; ; attempt++) {
      let res: Response;
      try {
        res = await send(path, await build());
      } catch (err) {
        if (err instanceof ApiError && err.code === 'NETWORK' && attempt < maxAttempts) {
          await sleep(300 * 2 ** (attempt - 1));
          continue;
        }
        throw err;
      }

      if (res.status === 401 && options.auth && attempt === 1) {
        // Access token expired or session rotated elsewhere: refresh once and retry.
        const next = await refresh();
        if (!next && options.auth === true) throw new ApiError('UNAUTHENTICATED', 'Please sign in to continue.', 401);
        continue; // optional auth: retry anonymously if the session is gone
      }
      if (RETRYABLE_STATUS.has(res.status) && attempt < maxAttempts) {
        await sleep(300 * 2 ** (attempt - 1));
        continue;
      }
      if (!res.ok) throw await toError(res);
      if (res.status === 204) return undefined as T;
      if (options.responseType === 'blob') return (await res.blob()) as T;
      return (await res.json()) as T;
    }
  }

  async function authenticate(path: string, body: unknown): Promise<AuthResponse> {
    const res = await send(`${opts.authPath}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await toError(res);
    const next = (await res.json()) as AuthResponse;
    setSession(next);
    return next;
  }

  return {
    request,
    getSession: () => session,
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /** Restores a session from the httpOnly refresh cookie on app start. Never throws on 401. */
    restore: () => refresh(),
    login: (body: LoginRequest) => authenticate('/login', body),
    register: (body: RegisterRequest) => authenticate('/register', body),
    async logout() {
      try {
        await send(`${opts.authPath}/logout`, { method: 'POST', headers: { [CSRF_HEADER]: '1' } });
      } finally {
        setSession(null);
      }
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
